import { isDefined } from "@clipboard-health/util-ts";
import { remove } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { generatePath } from "react-router-dom";
import { z } from "zod";

import { invalidateShifts } from "../../invalidateShifts";

const DELETE_SHOPPABLE_QUALIFICATION_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/workers/:workerId/shoppable-qualifications/:id`;

interface UseDeleteShoppableQualificationParams {
  workerId: string;
  shoppableQualificationId: string;
}

export function useDeleteShoppableQualification(
  options?: UseMutationOptions<void, AxiosError<void>, UseDeleteShoppableQualificationParams>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UseDeleteShoppableQualificationParams) => {
      await remove({
        url: generatePath(DELETE_SHOPPABLE_QUALIFICATION_URL, {
          workerId: params.workerId,
          id: params.shoppableQualificationId,
        }),
        responseSchema: z.unknown(),
      });
    },
    onSuccess: (_, params) => {
      queryClient.setQueryData([GET_WORKER_QUERY_KEY], (oldData: Worker | undefined) => {
        if (!isDefined(oldData)) {
          return oldData;
        }

        return {
          ...oldData,
          shoppableQualifications: oldData.shoppableQualifications.filter(
            (shoppableQualification) =>
              shoppableQualification.id !== params.shoppableQualificationId
          ),
        };
      });

      // need to invalidate shifts to get the new roles in the shifts list
      void invalidateShifts(queryClient);
    },
    ...options,
  });
}
